/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ACTIVE_UPLOAD_STATUSES, MultipartUploadState, UploadStatus } from "../utils/types";
import { buildMultipartUploadState, uploadMultipartPartsToS3 } from "../utils/multipartUpload";
import CollateralService from "../services/collaterals/CollateralService";
import useDisplaySnackbar from "../utils/useDisplaySnackbar";
import { GlobalContext } from "./GlobalContext";
import { allowedListToUpload } from "../utils/utils";

export interface PendingMultipartUpload {
  uploadId: string;
  objectKey: string;
  interactionId?: string;
  status?: string;
}

const MULTIPART_UPLOAD_SESSION_KEY = "multipart-upload";

declare global {
  interface Window {
    __uploadFirstMountDone?: boolean;
  }
}

export const readPendingMultipartUploads = (): PendingMultipartUpload[] => {
  const pendingUpload = sessionStorage.getItem(MULTIPART_UPLOAD_SESSION_KEY);
  if (!pendingUpload) return [];

  try {
    const parsed = JSON.parse(pendingUpload);
    if (Array.isArray(parsed)) {
      return parsed.filter(
        (item) =>
          item &&
          typeof item.uploadId === "string" &&
          typeof item.objectKey === "string"
      );
    }
    if (
      parsed &&
      typeof parsed.uploadId === "string" &&
      typeof parsed.objectKey === "string"
    ) {
      return [
        {
          uploadId: parsed.uploadId,
          objectKey: parsed.objectKey,
          interactionId: parsed.interactionId,
          status: parsed.status ?? "starting",
        },
      ];
    }
  } catch {
    return [];
  }
  return [];
};

export const persistPendingMultipartUploads = (uploads: PendingMultipartUpload[]) => {
  if (uploads.length === 0) {
    sessionStorage.removeItem(MULTIPART_UPLOAD_SESSION_KEY);
    return;
  }
  sessionStorage.setItem(MULTIPART_UPLOAD_SESSION_KEY, JSON.stringify(uploads));
};

export interface UploadActions {
  startUpload: (
    file: File,
    interactionId: string,
    options?: { onComplete?: () => void; onError?: (err: any) => void }
  ) => void;
  cancelUpload: (uploadId: string) => Promise<void>;
  retryUpload: (uploadId: string) => void;
  abortAllUploads: () => Promise<void>;
}

export const UploadStateContext = createContext<MultipartUploadState[]>([]);
export const UploadActionsContext = createContext<UploadActions>({
  startUpload: () => {},
  cancelUpload: async () => {},
  retryUpload: () => {},
  abortAllUploads: async () => {},
});

export const useUploadState = () => useContext(UploadStateContext);
export const useUploadActions = () => useContext(UploadActionsContext);

// Backwards compatibility hook
export const useUploadContext = () => {
  const multipartUploadState = useUploadState();
  const actions = useUploadActions();
  return {
    multipartUploadState,
    ...actions,
  };
};

interface UploadContextProviderProps {
  children: React.ReactNode;
}

export const UploadContextProvider: React.FC<UploadContextProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useDisplaySnackbar();
  const { accountDetails } = useContext(GlobalContext);
  const [multipartUploadState, setMultipartUploadState] = useState<MultipartUploadState[]>([]);

  // Keep a ref to latest state so callbacks read current values without stale closures
  const stateRef = useRef<MultipartUploadState[]>([]);
  stateRef.current = multipartUploadState;

  // Active in-flight AbortControllers and Cancelled IDs
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const cancelledIdsRef = useRef<Set<string>>(new Set());
  const completedUploadIdsRef = useRef<Set<string>>(new Set());
  const inFlightStartPromisesRef = useRef<Map<string, Promise<any>>>(new Map());

  // Progress throttle & trailing flush trackers
  const lastProgressUpdateRef = useRef<Map<string, number>>(new Map());
  const lastStatusRef = useRef<Map<string, string>>(new Map());
  const trailingTimeoutRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const { mutateAsync: startMultipartUploadApi } = CollateralService.usePostAssetMultipartUploadStart();
  const { mutateAsync: completeMultipartUploadApi } = CollateralService.usePostAssetMultipartUploadComplete();
  const { mutateAsync: refreshUploadUrlsApi } = CollateralService.usePostAssetMultipartRefreshUrls();
  const { mutateAsync: abortMultipartUploadApi } = CollateralService.usePostAssetMultipartAbort();

  const upsertUploadState = (nextState: MultipartUploadState) => {
    setMultipartUploadState((prev) => {
      const exists = prev.some((s) => s.uploadId === nextState.uploadId);
      return exists
        ? prev.map((s) => (s.uploadId === nextState.uploadId ? nextState : s))
        : [...prev, nextState];
    });
  };

  const removeUploadState = (uploadId: string) => {
    setMultipartUploadState((prev) => prev.filter((s) => s.uploadId !== uploadId));
    lastProgressUpdateRef.current.delete(uploadId);
    lastStatusRef.current.delete(uploadId);
    const timer = trailingTimeoutRef.current.get(uploadId);
    if (timer) clearTimeout(timer);
    trailingTimeoutRef.current.delete(uploadId);
  };

  const updateUploadStateThrottled = (
    uploadId: string,
    updater: (prev: MultipartUploadState) => MultipartUploadState,
    forceImmediate = false
  ) => {
    const now = Date.now();
    const lastUpdate = lastProgressUpdateRef.current.get(uploadId) || 0;
    const currentTarget = stateRef.current.find((s) => s.uploadId === uploadId);
    const prevStatus = lastStatusRef.current.get(uploadId) || currentTarget?.status || "starting";

    const nextStateDraft = updater(
      currentTarget || {
        uploadId,
        objectKey: "",
        interactionId: "",
        file: new File([], ""),
        fileName: "",
        fileSize: 0,
        partSize: 0,
        parts: [],
        uploadedBytes: 0,
        progress: 0,
        status: UploadStatus.STARTING
      }
    );

    const isStatusChanged = forceImmediate || nextStateDraft.status !== prevStatus;

    if (isStatusChanged) {
      const existingTimeout = trailingTimeoutRef.current.get(uploadId);
      if (existingTimeout) clearTimeout(existingTimeout);

      lastStatusRef.current.set(uploadId, nextStateDraft.status);
      lastProgressUpdateRef.current.set(uploadId, now);

      setMultipartUploadState((prev) =>
        prev.map((state) => (state.uploadId === uploadId ? updater(state) : state))
      );
      return;
    }

    if (now - lastUpdate >= 250) {
      lastProgressUpdateRef.current.set(uploadId, now);
      setMultipartUploadState((prev) =>
        prev.map((state) => (state.uploadId === uploadId ? updater(state) : state))
      );
    } else {
      const existingTimeout = trailingTimeoutRef.current.get(uploadId);
      if (existingTimeout) clearTimeout(existingTimeout);

      const timer = setTimeout(() => {
        setMultipartUploadState((prev) =>
          prev.map((state) => (state.uploadId === uploadId ? updater(state) : state))
        );
      }, 250);

      trailingTimeoutRef.current.set(uploadId, timer);
    }
  };

  const startUpload = useCallback(
    async (
      file: File,
      interactionId: string,
      options?: { onComplete?: () => void; onError?: (err: any) => void }
    ) => {
      if (file.size === 0) {
        showSnackbar("error", "Cannot upload empty file.");
        return;
      }

      const hasExtension = file.name.includes(".") && file.name.lastIndexOf(".") > 0;
      const fileExtension = hasExtension ? file.name.split(".").pop()?.toLowerCase() || "" : "";
      if (allowedListToUpload.length > 0 && (!hasExtension || !allowedListToUpload.includes(fileExtension))) {
        const errorMessage = fileExtension
          ? `The file type '${fileExtension}' is not allowed for upload`
          : "This file type is not allowed for upload";
        showSnackbar("error", errorMessage);
        options?.onError?.(new Error(errorMessage));
        return;
      }

      const tempUploadId = `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      let activeUploadId = tempUploadId;

      const initialUploadState: MultipartUploadState = {
        uploadId: tempUploadId,
        objectKey: "",
        interactionId,
        file,
        fileName: file.name,
        fileSize: file.size,
        partSize: file.size,
        parts: [],
        uploadedBytes: 0,
        progress: 0,
        status: UploadStatus.STARTING
      };

      upsertUploadState(initialUploadState);

      const uploadAbortController = new AbortController();
      abortControllersRef.current.set(tempUploadId, uploadAbortController);

      const startPromise = startMultipartUploadApi({
        interactionId,
        file,
        interactionCollateralsRequest: {
          assetSource: "Account",
          assetType: "other",
          assetName: file.name,
          createdBy: accountDetails?.profileOrgId ?? "",
          aiAnalysisStatus: "",
          uploadStatus: "Pending",
        },
        tempUploadId,
        signal: uploadAbortController.signal,
      });

      inFlightStartPromisesRef.current.set(tempUploadId, startPromise);

      try {
        let rawData: any;
        try {
          rawData = await startPromise;
        } finally {
          inFlightStartPromisesRef.current.delete(tempUploadId);
        }

        // Unwrap response envelope safely
        const data = rawData?.uploadId ? rawData : (rawData?.data ?? rawData);

        if (cancelledIdsRef.current.has(tempUploadId) || uploadAbortController.signal.aborted) {
          if (data?.uploadId && data?.objectKey && !data?.isCompleted) {
            try {
              await abortMultipartUploadApi({
                uploadId: data.uploadId,
                objectKey: data.objectKey,
              });
            } catch {
              // ignore
            }
          }
          removeUploadState(tempUploadId);
          cancelledIdsRef.current.delete(tempUploadId);
          if (data?.uploadId) cancelledIdsRef.current.delete(data.uploadId);
          abortControllersRef.current.delete(tempUploadId);
          return;
        }

        // Small file direct upload path
        if (data?.isCompleted && data?.assetId) {
          completedUploadIdsRef.current.add(tempUploadId);
          if (data.uploadId) completedUploadIdsRef.current.add(data.uploadId);
          abortControllersRef.current.delete(tempUploadId);
          cancelledIdsRef.current.delete(tempUploadId);
          removeUploadState(tempUploadId);
          queryClient.invalidateQueries({
            queryKey: ["get_interaction_collateral"],
            refetchType: "active",
          });
          showSnackbar("success", `${file.name} uploaded successfully`);
          options?.onComplete?.();
          return;
        }

        // Large file multipart upload path
        const uploadState = buildMultipartUploadState(data, file, interactionId);
        activeUploadId = uploadState.uploadId;

        if (cancelledIdsRef.current.has(tempUploadId)) {
          cancelledIdsRef.current.add(activeUploadId);
        }

        abortControllersRef.current.set(activeUploadId, uploadAbortController);
        abortControllersRef.current.delete(tempUploadId);
        removeUploadState(tempUploadId);

        if (cancelledIdsRef.current.has(activeUploadId) || uploadAbortController.signal.aborted) {
          if (uploadState.uploadId && uploadState.objectKey) {
            try {
              await abortMultipartUploadApi({
                uploadId: uploadState.uploadId,
                objectKey: uploadState.objectKey,
              });
            } catch {
              // ignore failure
            }
          }
          removeUploadState(tempUploadId);
          removeUploadState(activeUploadId);
          cancelledIdsRef.current.delete(tempUploadId);
          cancelledIdsRef.current.delete(activeUploadId);
          abortControllersRef.current.delete(tempUploadId);
          abortControllersRef.current.delete(activeUploadId);
          return;
        }

        upsertUploadState(uploadState);

        persistPendingMultipartUploads([
          ...readPendingMultipartUploads().filter((item) => item.uploadId !== uploadState.uploadId),
          {
            uploadId: uploadState.uploadId,
            objectKey: uploadState.objectKey,
            interactionId: uploadState.interactionId,
            status: uploadState.status,
          },
        ]);

        const completedParts = await uploadMultipartPartsToS3(
          uploadState,
          (updatedState) => {
            if (cancelledIdsRef.current.has(activeUploadId)) return;
            updateUploadStateThrottled(
              uploadState.uploadId,
              () => updatedState
            );
          },
          async (uploadId: string, objectKey: string, partNumbers: number[]) => {
            const response = await refreshUploadUrlsApi({
              uploadId,
              objectKey,
              partNumbers,
            });
            return response?.parts ?? response?.data?.parts ?? [];
          },
          async (uploadId: string, objectKey: string) => {
            await abortMultipartUploadApi({
              uploadId,
              objectKey,
            });
          },
          uploadAbortController.signal
        );

        if (cancelledIdsRef.current.has(activeUploadId) || uploadAbortController.signal.aborted) {
          removeUploadState(activeUploadId);
          cancelledIdsRef.current.delete(tempUploadId);
          cancelledIdsRef.current.delete(activeUploadId);
          abortControllersRef.current.delete(activeUploadId);
          persistPendingMultipartUploads(
            readPendingMultipartUploads().filter((item) => item.uploadId !== activeUploadId)
          );
          return;
        }

        // Set completing status before Complete API call
        updateUploadStateThrottled(
          uploadState.uploadId,
          (prev) => ({ ...prev, status: UploadStatus.COMPLETING, progress: 99 }),
          true
        );

        try {
          await completeMultipartUploadApi({
            uploadId: uploadState.uploadId,
            objectKey: uploadState.objectKey,
            parts: completedParts,
          });
        } catch (completeErr) {
          if (!cancelledIdsRef.current.has(activeUploadId)) {
            try {
              await abortMultipartUploadApi({
                uploadId: uploadState.uploadId,
                objectKey: uploadState.objectKey,
              });
            } catch {
              // ignore failure
            }
          }
          throw completeErr;
        }

        // Instantly remove row on success
        completedUploadIdsRef.current.add(uploadState.uploadId);
        completedUploadIdsRef.current.add(tempUploadId);

        persistPendingMultipartUploads(
          readPendingMultipartUploads().filter((item) => item.uploadId !== uploadState.uploadId)
        );
        removeUploadState(uploadState.uploadId);
        abortControllersRef.current.delete(uploadState.uploadId);
        cancelledIdsRef.current.delete(tempUploadId);
        cancelledIdsRef.current.delete(uploadState.uploadId);

        queryClient.invalidateQueries({
          queryKey: ["get_interaction_collateral"],
          refetchType: "active",
        });
        showSnackbar("success", `${file.name} uploaded successfully`);
        options?.onComplete?.();
      } catch (err: any) {
        if (cancelledIdsRef.current.has(activeUploadId) || cancelledIdsRef.current.has(tempUploadId) || uploadAbortController.signal.aborted) {
          removeUploadState(tempUploadId);
          removeUploadState(activeUploadId);
          cancelledIdsRef.current.delete(tempUploadId);
          cancelledIdsRef.current.delete(activeUploadId);
          abortControllersRef.current.delete(tempUploadId);
          abortControllersRef.current.delete(activeUploadId);
          persistPendingMultipartUploads(
            readPendingMultipartUploads().filter((item) => item.uploadId !== activeUploadId && item.uploadId !== tempUploadId)
          );
          return;
        }

        const rawError =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.response?.data?.errors ||
          (typeof err?.response?.data === "string" ? err?.response?.data : "") ||
          err?.message ||
          "Upload failed";

        const errorMessage =
          typeof rawError === "string"
            ? rawError
            : Array.isArray(rawError) || typeof rawError === "object"
            ? JSON.stringify(rawError)
            : String(rawError);

        const lowerMsg = errorMessage.toLowerCase();
        const isNotAllowedError =
          lowerMsg.includes("not allowed") ||
          lowerMsg.includes("is not allowed") ||
          lowerMsg.includes("file type") ||
          lowerMsg.includes("extension");

        const activeItem = stateRef.current.find((s) => s.uploadId === activeUploadId);

        if (isNotAllowedError || !activeItem) {
          removeUploadState(tempUploadId);
          removeUploadState(activeUploadId);
          cancelledIdsRef.current.delete(tempUploadId);
          cancelledIdsRef.current.delete(activeUploadId);
          abortControllersRef.current.delete(tempUploadId);
          abortControllersRef.current.delete(activeUploadId);
          persistPendingMultipartUploads(
            readPendingMultipartUploads().filter(
              (item) => item.uploadId !== activeUploadId && item.uploadId !== tempUploadId
            )
          );
        } else {
          updateUploadStateThrottled(
            activeItem.uploadId,
            (prev) => ({
              ...prev,
              status: UploadStatus.FAILED,
              errorMessage,
            }),
            true
          );
          persistPendingMultipartUploads(
            readPendingMultipartUploads().filter((item) => item.uploadId !== activeItem.uploadId)
          );
        }

        showSnackbar("error", errorMessage);
        options?.onError?.(err);
      }
    },
    [
      accountDetails?.profileOrgId,
      startMultipartUploadApi,
      completeMultipartUploadApi,
      refreshUploadUrlsApi,
      abortMultipartUploadApi,
      queryClient,
      showSnackbar,
    ]
  );

  const cancelUpload = useCallback(
    async (uploadId: string) => {
      if (completedUploadIdsRef.current.has(uploadId)) {
        return;
      }

      cancelledIdsRef.current.add(uploadId);
      const controller = abortControllersRef.current.get(uploadId);
      if (controller) {
        controller.abort();
        abortControllersRef.current.delete(uploadId);
      }

      const target = stateRef.current.find((s) => s.uploadId === uploadId);
      removeUploadState(uploadId);
      persistPendingMultipartUploads(
        readPendingMultipartUploads().filter((item) => item.uploadId !== uploadId)
      );

      if (target?.objectKey && !completedUploadIdsRef.current.has(target.uploadId)) {
        try {
          await abortMultipartUploadApi({
            uploadId: target.uploadId,
            objectKey: target.objectKey,
          });
        } catch {
          // ignore failure
        }
      }
    },
    [abortMultipartUploadApi]
  );

  const retryUpload = useCallback(
    (uploadId: string) => {
      const target = stateRef.current.find((s) => s.uploadId === uploadId);
      if (!target) return;

      cancelledIdsRef.current.delete(uploadId);
      completedUploadIdsRef.current.delete(uploadId);
      removeUploadState(uploadId);
      persistPendingMultipartUploads(
        readPendingMultipartUploads().filter((item) => item.uploadId !== uploadId)
      );

      startUpload(target.file, target.interactionId);
    },
    [startUpload]
  );

  const abortAllUploads = useCallback(async () => {
    const activeUploads = stateRef.current.filter((s) =>
      ["starting", "uploading", "completing"].includes(s.status)
    );

    activeUploads.forEach((u) => {
      cancelledIdsRef.current.add(u.uploadId);
      const controller = abortControllersRef.current.get(u.uploadId);
      if (controller) controller.abort();
    });

    abortControllersRef.current.forEach((controller) => controller.abort());

    const pendingStartPromises = Array.from(inFlightStartPromisesRef.current.values());

    setMultipartUploadState([]);
    sessionStorage.removeItem(MULTIPART_UPLOAD_SESSION_KEY);

    const abortObjectKeyPromises = activeUploads.map((upload) =>
      upload.objectKey && !completedUploadIdsRef.current.has(upload.uploadId)
        ? abortMultipartUploadApi({ uploadId: upload.uploadId, objectKey: upload.objectKey })
        : Promise.resolve()
    );

    await Promise.allSettled([...abortObjectKeyPromises, ...pendingStartPromises]);
  }, [abortMultipartUploadApi]);

  // Attach global beforeunload listener
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const hasActive = stateRef.current.some((u) =>
        ACTIVE_UPLOAD_STATUSES.includes(u.status)
      );
      if (!hasActive) return;

      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Reload abort after session is ready
  useEffect(() => {
    if (window.__uploadFirstMountDone) return;
    window.__uploadFirstMountDone = true;

    const isReload =
      (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type === "reload" ||
      (performance as any)?.navigation?.type === 1;

    if (!isReload) return;

    const pendingUploads = readPendingMultipartUploads();
    if (!pendingUploads.length) return;

    Promise.allSettled(
      pendingUploads.map(({ uploadId, objectKey }) =>
        abortMultipartUploadApi({ uploadId, objectKey })
      )
    ).finally(() => {
      persistPendingMultipartUploads([]);
      showSnackbar("warning", "Uploads were cancelled because the page was refreshed.");
    });
  }, [abortMultipartUploadApi, showSnackbar]);

  const actionsRef = useRef<UploadActions>({
    startUpload,
    cancelUpload,
    retryUpload,
    abortAllUploads,
  });

  actionsRef.current.startUpload = startUpload;
  actionsRef.current.cancelUpload = cancelUpload;
  actionsRef.current.retryUpload = retryUpload;
  actionsRef.current.abortAllUploads = abortAllUploads;

  return (
    <UploadStateContext.Provider value={multipartUploadState}>
      <UploadActionsContext.Provider value={actionsRef.current}>
        {children}
      </UploadActionsContext.Provider>
    </UploadStateContext.Provider>
  );
};
